import { getApiHeaders } from '../utils/apiHeaders';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL as string;
const PACKAGE_ORDERS_PATH = `${API_BASE_URL}/api/package-orders`;

type PackageOrder = {
  id: string;
  publicReference?: string;
  packageId?: string;
  customerId?: number;
  queryId?: number;
  createdBy?: number;
  assignedTo?: number;
  currency?: string;
  totalAmountPaise?: number;
  totalAmountReceivablePaise?: number;
  totalAmountPayablePaise?: number;
  paidAmountPaise?: number;
  orderStatus?: string;
  paymentStatus?: string;
  fulfilmentStatus?: string;
  travelStartDate?: string;
  travelEndDate?: string;
  createdAt?: string;
  updatedAt?: string;
  customer?: { id?: number; name?: string };
  package?: { id?: string; title?: string };
  payments?: Array<{ id: string; provider?: string; amountPaise?: number; status?: string; createdAt?: string; providerPaymentId?: string }>;
  fulfilmentPlan?: Record<string, unknown>;
  lineItems?: Array<any>;
  travellerSnapshot?: Record<string, unknown>;
  contactSnapshot?: Record<string, unknown>;
};

export const filterBookingsForOps = <T extends { assigned_to?: number | string; created_by?: number | string; user?: { id?: number | string } }>(
  bookings: T[],
  userId?: number | string,
) => {
  if (userId === undefined || userId === null) return bookings;
  const id = String(userId);
  return bookings.filter((booking) =>
    String(booking.assigned_to ?? booking.user?.id ?? '') === id
    || String(booking.created_by ?? '') === id,
  );
};

const toLegacyBooking = (order: PackageOrder) => {
  const totalAmountReceivablePaise = Number(order.totalAmountPaise || 0);
  const totalAmountPayablePaise = Number(order.totalAmountPayablePaise || 0);
  const paidAmountPaise = Number(order.paidAmountPaise || 0);
  return {
    id: order.id,
    booking_reference: order.publicReference,
    assigned_to: order.assignedTo,
    created_by: order.createdBy,
    query_id: order.queryId,
    customer_id: order.customerId,
    status: order.orderStatus || 'DRAFT',
    payment_status: order.paymentStatus || 'UNPAID',
    created_at: order.createdAt,
    updated_at: order.updatedAt,
    travel_start_date: order.travelStartDate,
    travel_end_date: order.travelEndDate,
    total_price: totalAmountReceivablePaise / 100,
    currency: order.currency || 'INR',
    product_type: 'package',
    total_amount_receivable_paise: totalAmountReceivablePaise,
    total_amount_payable_paise: totalAmountPayablePaise,
    // Package orders store the two operator-entered financial values. Markup
    // is derived, never independently persisted or trusted from the client.
    markup_paise: totalAmountReceivablePaise - totalAmountPayablePaise,
    // The backend exposes the persisted quotation snapshot as fulfilmentPlan.
    // Keep the legacy booking field populated so BookingsPage can render its
    // components after a draft is materialized.
    booking_plan: order.fulfilmentPlan || {},
    line_items: order.lineItems || [],
    travellerSnapshot: order.travellerSnapshot || {},
    contactSnapshot: order.contactSnapshot || {},
    payment_details: {
      booking_amount: totalAmountReceivablePaise,
      payment_amount: paidAmountPaise / 100,
    },
    package: { id: order.package?.id || order.packageId, title: order.package?.title || 'Package order' },
    customer: order.customer ? { id: order.customer.id, name: order.customer.name || 'Unknown', email: '', phoneNumber: '' } : undefined,
    payments: (order.payments || []).map((payment) => ({
      id: payment.id,
      amount: Number(payment.amountPaise || 0) / 100,
      method: payment.provider || 'OFFLINE',
      status: payment.status || '',
      created_at: payment.createdAt,
      transaction_id: payment.providerPaymentId || null,
    })),
  } as any;
};

export const fetchBookings = async (_userId?: number | string) => {
  const url = new URL(PACKAGE_ORDERS_PATH);
  url.searchParams.set('pageSize', '100');
  const res = await fetch(url.toString(), { headers: getApiHeaders('none') });
  if (!res.ok) throw new Error(`Failed to fetch package orders: ${res.status}`);
  const data = await res.json();
  // The server is the authorization authority. The UI must not infer scope
  // from a decoded browser token or hide records as an access control.
  return (Array.isArray(data?.items) ? data.items : []).map(toLegacyBooking);
};

export async function assignBooking(bookingId: number | string, userId: number): Promise<{ id: number | string }> {
  const res = await fetch(`${PACKAGE_ORDERS_PATH}/${encodeURIComponent(String(bookingId))}`, {
    method: 'PATCH', headers: getApiHeaders('json'), body: JSON.stringify({ assignedTo: userId }),
  });
  if (!res.ok) throw new Error(`Failed to assign package order: ${await res.text()}`);
  return { id: (await res.json()).id };
}

export async function addPayment(bookingId: number | string, paymentData: { amount: number; method: string; transactionId: string; notes?: string; }): Promise<{ id: number | string }> {
  const res = await fetch(`${PACKAGE_ORDERS_PATH}/${encodeURIComponent(String(bookingId))}/payments/offline`, {
    method: 'POST', headers: getApiHeaders('json'), body: JSON.stringify({ amountPaise: paymentData.amount, transactionId: paymentData.transactionId, note: paymentData.notes }),
  });
  if (!res.ok) throw new Error(`Failed to record payment: ${await res.text()}`);
  return { id: (await res.json()).id };
}

export async function paymentReminder(): Promise<never> {
  throw new Error('Payment reminders are not part of the package-order API yet');
}

export interface CreateBookingPayload {
  packageId?: string;
  customerId: number | string;
  queryId?: number | string;
  passengers?: { adults: number; children?: number };
  travelStartDate?: string;
  travelEndDate?: string;
  notes?: string;
  assignedTo?: number | string;
  totalAmountReceivablePaise?: number;
  totalAmountPayablePaise?: number;
}

export async function createBooking(payload: CreateBookingPayload) {
  const res = await fetch(PACKAGE_ORDERS_PATH, {
    method: 'POST', headers: getApiHeaders('json'), body: JSON.stringify({
      packageId: payload.packageId, customerId: payload.customerId, queryId: payload.queryId,
      passengers: payload.passengers, travelStartDate: payload.travelStartDate, travelEndDate: payload.travelEndDate,
      assignedTo: payload.assignedTo, selectionSnapshot: payload.notes ? { crmNote: payload.notes } : {},
      totalAmountReceivablePaise: payload.totalAmountReceivablePaise,
      totalAmountPayablePaise: payload.totalAmountPayablePaise,
    }),
  });
  if (!res.ok) throw new Error(`Failed to create package order: ${await res.text()}`);
  return res.json();
}

export async function createBookingFromQuery(queryId: number, payload: Omit<CreateBookingPayload, 'queryId'>) {
  return createBooking({ ...payload, queryId });
}

export async function updateBooking(bookingId: number | string, payload: { status?: string; assignedTo?: number | string }) {
  const requestedStatus = payload.status?.toUpperCase();
  const orderStatus = requestedStatus === 'CANCELLED' ? 'CANCELLED' : requestedStatus === 'DRAFT' ? 'DRAFT' : requestedStatus ? 'PAYMENT_PENDING' : undefined;
  const res = await fetch(`${PACKAGE_ORDERS_PATH}/${encodeURIComponent(String(bookingId))}`, {
    method: 'PATCH', headers: getApiHeaders('json'), body: JSON.stringify({ ...(orderStatus ? { orderStatus } : {}), ...(payload.assignedTo ? { assignedTo: payload.assignedTo } : {}) }),
  });
  if (!res.ok) throw new Error(`Failed to update package order: ${await res.text()}`);
  return toLegacyBooking(await res.json());
}

export async function fetchBookingById(bookingId: number | string): Promise<any> {
  const res = await fetch(`${PACKAGE_ORDERS_PATH}/${encodeURIComponent(String(bookingId))}`, { headers: getApiHeaders('none') });
  if (!res.ok) throw new Error(`Failed to fetch package order: ${await res.text()}`);
  const legacy = toLegacyBooking(await res.json() as PackageOrder);
  return {
    ...legacy,
    payment_history: (legacy.payments || []).map((payment: any) => ({
      id: payment.id, amount_paise: Math.round(Number(payment.amount || 0) * 100), provider: payment.method,
      status: payment.status, created_at: payment.created_at, notes: { transactionId: payment.transaction_id },
    })),
  };
}

export async function updateBookingDetails(bookingId: number | string, payload: { travellers: any[]; contact: { email: string; phone: string } }) {
  const res = await fetch(`${PACKAGE_ORDERS_PATH}/${encodeURIComponent(String(bookingId))}/booking-details`, { method: 'PUT', headers: getApiHeaders('json'), body: JSON.stringify(payload) });
  if (!res.ok) throw new Error(`Failed to save booking details: ${await res.text()}`);
  return res.json();
}

const componentAction = async (bookingId: number | string, lineItemId: string, action: string, payload?: Record<string, unknown>) => {
  const res = await fetch(`${PACKAGE_ORDERS_PATH}/${encodeURIComponent(String(bookingId))}/line-items/${encodeURIComponent(lineItemId)}/${action}`, { method: 'POST', headers: getApiHeaders('json'), ...(payload ? { body: JSON.stringify(payload) } : {}) });
  if (!res.ok) throw new Error(`Component booking action failed: ${await res.text()}`);
  return res.json();
};
export const prepareBookingComponent = (bookingId: number | string, lineItemId: string) => componentAction(bookingId, lineItemId, 'prepare-booking');
export const acceptBookingComponentReview = (bookingId: number | string, lineItemId: string) => componentAction(bookingId, lineItemId, 'accept-booking-review');
export const requestBookingComponentOtp = (bookingId: number | string, lineItemId: string) => componentAction(bookingId, lineItemId, 'request-booking-otp');
export const executeBookingComponent = (bookingId: number | string, lineItemId: string, code: string) => componentAction(bookingId, lineItemId, 'execute-booking', { code });
